/**
 * Top-level providers (Query, ErrorBoundary). Mounted by main.tsx so
 * test setups can re-use the same provider tree by importing it.
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { useState, type ReactNode } from 'react';

import { AppErrorBoundary } from '@/components/layout/error-boundary';
import { ConfirmDialogProvider } from '@/components/ui/confirm-dialog/confirm-dialog';
import { analyticsEnabled } from '@/lib/analytics';

const MAX_RETRIES = 3;

function isClientError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return false;
  }
  const { status } = error;
  return typeof status === 'number' && status >= 400 && status < 500;
}

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failureCount, error) => {
          // 4xx errors (auth, validation) are not transient; do not retry.
          // 401 is already swallowed-and-refreshed inside apiFetch before
          // it can surface as an error here.
          if (isClientError(error)) {
            return false;
          }
          // 5xx and network failures (no `status` on the error) — retry a
          // few times with the built-in exponential backoff before showing
          // the per-widget error UI.
          return failureCount < MAX_RETRIES;
        },
        refetchOnWindowFocus: false,
      },
      mutations: { retry: 0 },
    },
  });
}

interface AppProvidersProps {
  children: ReactNode;
}

export function AppProviders({ children }: AppProvidersProps) {
  const [client] = useState(createQueryClient);

  const tree = (
    <AppErrorBoundary>
      <QueryClientProvider client={client}>
        <ConfirmDialogProvider>{children}</ConfirmDialogProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );

  // PostHog is initialised in main.tsx (initAnalytics). The provider only
  // exposes the already-configured client to React hooks; when analytics is
  // disabled (no key, local/E2E) we skip it entirely so nothing is recorded.
  return analyticsEnabled ? <PostHogProvider client={posthog}>{tree}</PostHogProvider> : tree;
}
